import { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAllItemTypesQuery } from '@/graphql/queries/allItemTypes.generated';
import {
  ContainerTypeSegment,
  ItemTypeCategory,
} from '@/graphql/generatedTypes';
import {
  RevisionFormState,
  AllowanceFormEntry,
  FreeCheckoutFormEntry,
  isRevisionFormValid,
  formatCents,
  SEGMENT_LABELS,
} from '../types';
import i18n from '@/utils/i18n';

interface ServicePackageRevisionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The contract term this revision belongs to — shown but not editable */
  term: number;
  isFirstRevision: boolean;
  form: RevisionFormState;
  onChange: (form: RevisionFormState) => void;
  onSave: () => void;
  saving: boolean;
  currency?: string;
  /** When true, free checkouts are limited to a single segment (store-level editing) */
  singleSegment?: boolean;
}

export function ServicePackageRevisionDialog({
  open,
  onOpenChange,
  term,
  isFirstRevision,
  form,
  onChange,
  onSave,
  saving,
  currency = 'EUR',
  singleSegment = false,
}: ServicePackageRevisionDialogProps) {
  const [pendingTypeId, setPendingTypeId] = useState('');
  const [pendingAmount, setPendingAmount] = useState('0');
  const [pendingSegment, setPendingSegment] = useState('');
  const [pendingSegmentAmount, setPendingSegmentAmount] = useState('0');

  const { data: itemTypesData } = useAllItemTypesQuery({
    skip: !open,
    variables: {
      filter: { category: ItemTypeCategory.StoreAsset },
      skip: 0,
      take: 1000,
    },
  });
  const allItemTypes = itemTypesData?.itemTypes.items ?? [];

  const set = <K extends keyof RevisionFormState>(
    key: K,
    value: RevisionFormState[K],
  ) => onChange({ ...form, [key]: value });

  // ── Allowance helpers ─────────────────────────────────────────────────────

  const removeAllowance = (item_type_id: number) =>
    set(
      'asset_allowances',
      form.asset_allowances.filter((a) => a.item_type_id !== item_type_id),
    );

  const updateAllowanceAmount = (item_type_id: number, amount: string) =>
    set(
      'asset_allowances',
      form.asset_allowances.map((a) =>
        a.item_type_id === item_type_id ? { ...a, included_amount: amount } : a,
      ),
    );

  const addAllowance = () => {
    if (!pendingTypeId) return;
    const typeId = parseInt(pendingTypeId, 10);
    const typeName = allItemTypes.find((t) => t.id === typeId)?.name ?? '';
    const entry: AllowanceFormEntry = {
      item_type_id: typeId,
      item_type_name: typeName,
      included_amount: pendingAmount,
    };
    set('asset_allowances', [...form.asset_allowances, entry]);
    setPendingTypeId('');
    setPendingAmount('0');
  };

  const availableTypes = allItemTypes.filter(
    (t) => !form.asset_allowances.some((a) => a.item_type_id === t.id),
  );

  // ── Helpers ───────────────────────────────────────────────────────────────

  const centsPreview = (raw: string) => {
    const n = parseInt(raw, 10);
    return !isNaN(n) && n >= 0 ? (
      <span className="tw:text-xs tw:text-muted-foreground">
        = {formatCents(n, currency)}
      </span>
    ) : null;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="tw:max-w-lg tw:max-h-[85vh] tw:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isFirstRevision
              ? i18n.t('ServicePackage:configureNMonthTerm', { n: term })
              : i18n.t('ServicePackage:editNMonthTerm', { n: term })}
          </DialogTitle>
        </DialogHeader>

        <div className="tw:flex tw:flex-col tw:gap-4 tw:py-2">
          {/* ── Contract term (read-only) ─────────────────────────────────── */}
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label htmlFor="term">
              {i18n.t('ServicePackage:contractTermMonths')}
            </Label>
            <Input
              id="term"
              type="number"
              value={term}
              disabled
              className="tw:bg-muted"
            />
          </div>

          {/* ── Pricing ──────────────────────────────────────────────────── */}
          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label htmlFor="monthly_fee">
              {i18n.t('ServicePackage:monthlyServiceFeeCents')}
            </Label>
            <Input
              id="monthly_fee"
              type="number"
              min={0}
              placeholder="e.g. 2900"
              value={form.monthly_service_fee_in_cents}
              onChange={(e) =>
                set('monthly_service_fee_in_cents', e.target.value)
              }
            />
            {centsPreview(form.monthly_service_fee_in_cents)}
          </div>

          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label htmlFor="onboarding_fee">
              {i18n.t('ServicePackage:onboardingFeeCents')}
            </Label>
            <Input
              id="onboarding_fee"
              type="number"
              min={0}
              placeholder="e.g. 0"
              value={form.onboarding_fee_in_cents}
              onChange={(e) => set('onboarding_fee_in_cents', e.target.value)}
            />
            {centsPreview(form.onboarding_fee_in_cents)}
          </div>

          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label htmlFor="ops_price">
              {i18n.t('ServicePackage:opsOrderPriceCents')}
            </Label>
            <Input
              id="ops_price"
              type="number"
              min={0}
              placeholder="e.g. 500"
              value={form.ops_order_price_in_cents}
              onChange={(e) => set('ops_order_price_in_cents', e.target.value)}
            />
            {centsPreview(form.ops_order_price_in_cents)}
          </div>

          {/* ── Free checkouts ────────────────────────────────────────── */}
          {singleSegment ? (
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label>{i18n.t('ServicePackage:freeCheckouts')}</Label>
              <div className="tw:flex tw:items-center tw:gap-2">
                <Select
                  value={form.free_checkouts[0]?.segment ?? ''}
                  onValueChange={(seg) =>
                    set('free_checkouts', [
                      {
                        segment: seg as ContainerTypeSegment,
                        amount: form.free_checkouts[0]?.amount ?? '0',
                      },
                    ])
                  }
                >
                  <SelectTrigger className="tw:flex-1">
                    <SelectValue
                      placeholder={i18n.t('ServicePackage:selectSegment')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(
                      Object.keys(SEGMENT_LABELS) as ContainerTypeSegment[]
                    ).map((s) => (
                      <SelectItem key={s} value={s}>
                        {SEGMENT_LABELS[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={0}
                  className="tw:w-24"
                  placeholder={i18n.t('ServicePackage:amount')}
                  value={form.free_checkouts[0]?.amount ?? ''}
                  onChange={(e) => {
                    const seg = form.free_checkouts[0]?.segment;
                    if (!seg) return;
                    set('free_checkouts', [
                      { segment: seg, amount: e.target.value },
                    ]);
                  }}
                  disabled={!form.free_checkouts[0]?.segment}
                />
              </div>
              <p className="tw:text-xs tw:text-muted-foreground">
                {i18n.t('ServicePackage:segmentSwitchHelp')}
              </p>
            </div>
          ) : (
            <div className="tw:flex tw:flex-col tw:gap-2">
              <Label>{i18n.t('ServicePackage:freeCheckoutsPerSegment')}</Label>

              {form.free_checkouts.length > 0 && (
                <div className="tw:rounded-md tw:border tw:overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="tw:pl-3 tw:h-9 tw:text-xs">
                          {i18n.t('ServicePackage:segment')}
                        </TableHead>
                        <TableHead className="tw:h-9 tw:text-xs">
                          {i18n.t('ServicePackage:amount')}
                        </TableHead>
                        <TableHead className="tw:w-[40px]" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.free_checkouts.map((fc) => (
                        <TableRow key={fc.segment}>
                          <TableCell className="tw:pl-3 tw:py-1.5 tw:text-sm">
                            {SEGMENT_LABELS[fc.segment]}
                          </TableCell>
                          <TableCell className="tw:py-1.5">
                            <Input
                              type="number"
                              min={0}
                              className="tw:h-7 tw:w-20 tw:text-sm"
                              value={fc.amount}
                              onChange={(e) =>
                                set(
                                  'free_checkouts',
                                  form.free_checkouts.map((entry) =>
                                    entry.segment === fc.segment
                                      ? { ...entry, amount: e.target.value }
                                      : entry,
                                  ),
                                )
                              }
                            />
                          </TableCell>
                          <TableCell className="tw:py-1.5 tw:pr-2">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() =>
                                set(
                                  'free_checkouts',
                                  form.free_checkouts.filter(
                                    (entry) => entry.segment !== fc.segment,
                                  ),
                                )
                              }
                            >
                              <Trash2 className="tw:size-3.5 tw:text-muted-foreground" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {(() => {
                const usedSegments = new Set(
                  form.free_checkouts.map((fc) => fc.segment),
                );
                const availableSegments = (
                  Object.keys(SEGMENT_LABELS) as ContainerTypeSegment[]
                ).filter((s) => !usedSegments.has(s));

                return availableSegments.length > 0 ? (
                  <div className="tw:flex tw:items-center tw:gap-2">
                    <Select
                      value={pendingSegment}
                      onValueChange={setPendingSegment}
                    >
                      <SelectTrigger className="tw:flex-1">
                        <SelectValue
                          placeholder={i18n.t('ServicePackage:selectSegment')}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {availableSegments.map((s) => (
                          <SelectItem key={s} value={s}>
                            {SEGMENT_LABELS[s]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={0}
                      className="tw:w-20"
                      value={pendingSegmentAmount}
                      onChange={(e) => setPendingSegmentAmount(e.target.value)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!pendingSegment}
                      onClick={() => {
                        if (!pendingSegment) return;
                        set('free_checkouts', [
                          ...form.free_checkouts,
                          {
                            segment: pendingSegment as ContainerTypeSegment,
                            amount: pendingSegmentAmount,
                          },
                        ]);
                        setPendingSegment('');
                        setPendingSegmentAmount('0');
                      }}
                    >
                      <Plus className="tw:size-4" />
                      {i18n.t('Common:add')}
                    </Button>
                  </div>
                ) : null;
              })()}

              {form.free_checkouts.length === 0 && (
                <p className="tw:text-xs tw:text-muted-foreground">
                  {i18n.t('ServicePackage:noFreeCheckoutsConfigured')}
                </p>
              )}
            </div>
          )}

          <div className="tw:flex tw:flex-col tw:gap-1.5">
            <Label htmlFor="note">
              {i18n.t('ServicePackage:noteOptional')}
            </Label>
            <Input
              id="note"
              placeholder="e.g. Updated pricing Q4"
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </div>

          {/* ── Included item types ───────────────────────────────────────── */}
          <div className="tw:flex tw:flex-col tw:gap-2">
            <Label>{i18n.t('ServicePackage:includedItemTypes')}</Label>

            {form.asset_allowances.length > 0 && (
              <div className="tw:rounded-md tw:border tw:overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="tw:pl-3 tw:h-9 tw:text-xs">
                        {i18n.t('ServicePackage:itemType')}
                      </TableHead>
                      <TableHead className="tw:h-9 tw:text-xs">
                        {i18n.t('ServicePackage:included')}
                      </TableHead>
                      <TableHead className="tw:w-[40px]" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {form.asset_allowances.map((a) => (
                      <TableRow key={a.item_type_id}>
                        <TableCell className="tw:pl-3 tw:py-1.5 tw:text-sm">
                          {a.item_type_name}
                        </TableCell>
                        <TableCell className="tw:py-1.5">
                          <Input
                            type="number"
                            min={0}
                            className="tw:h-7 tw:w-20 tw:text-sm"
                            value={a.included_amount}
                            onChange={(e) =>
                              updateAllowanceAmount(
                                a.item_type_id,
                                e.target.value,
                              )
                            }
                          />
                        </TableCell>
                        <TableCell className="tw:py-1.5 tw:pr-2">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => removeAllowance(a.item_type_id)}
                          >
                            <Trash2 className="tw:size-3.5 tw:text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {availableTypes.length > 0 && (
              <div className="tw:flex tw:items-center tw:gap-2">
                <Select value={pendingTypeId} onValueChange={setPendingTypeId}>
                  <SelectTrigger className="tw:flex-1">
                    <SelectValue
                      placeholder={i18n.t('ServicePackage:selectItemType')}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTypes.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={0}
                  className="tw:w-20"
                  value={pendingAmount}
                  onChange={(e) => setPendingAmount(e.target.value)}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addAllowance}
                  disabled={!pendingTypeId}
                >
                  <Plus className="tw:size-4" />
                  {i18n.t('Common:add')}
                </Button>
              </div>
            )}

            {form.asset_allowances.length === 0 &&
              availableTypes.length === 0 && (
                <p className="tw:text-xs tw:text-muted-foreground">
                  {i18n.t('ServicePackage:allItemTypesIncluded')}
                </p>
              )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            {i18n.t('Common:cancel')}
          </Button>
          <Button
            onClick={onSave}
            disabled={!isRevisionFormValid(form) || saving}
          >
            {saving ? i18n.t('Common:saving') : i18n.t('Common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
